import React, { useState, useEffect } from 'react';

import { IdNameObject, SlicerDimensions, SlicerSettings, GraphLayoutProps } from '../../../types';
import DropDownFilter from '../../../components/DropDownFilter';

interface Props {
  dimensions: SlicerDimensions[];
  onUpdateSettings: (value: SlicerSettings) => void;
  onUpdateValuePercentage: (value: string) => void;
  onUpdateGraphLayout: (value: GraphLayoutProps) => void;
  isPercentageValue: boolean;
  defaultMetric: IdNameObject;
  disableMenu?: boolean;
}

const SlicerMenu = ({
  dimensions,
  onUpdateSettings,
  onUpdateValuePercentage,
  onUpdateGraphLayout,
  isPercentageValue,
  defaultMetric,
  disableMenu
}: Props): React.ReactElement => {
  const defaultValuePercentageOptions: Array<IdNameObject> = [
    {
      id: 'value',
      name: 'Value'
    },
    {
      id: 'percentage',
      name: 'Percentage'
    }
  ];
  const onlyValuePercentageOption: Array<IdNameObject> = [
    {
      id: 'value',
      name: 'Value'
    }
  ];

  const [sliceByValue, setSliceByValue] = useState<string | null>(null);
  const [selectedMetric, setSelectedMetric] = useState<IdNameObject | null>(defaultMetric);
  const [valuePercentageOptions, setValuePercentageOptions] =
    useState<Array<IdNameObject>>(defaultValuePercentageOptions);
  const [valuePercentage, setValuePercentage] = useState<IdNameObject | null>(null);
  const [graphLayout, setGraphLayout] = useState<GraphLayoutProps | null>(null);

  const [dimensionMetrics, setDimensionMetrics] = useState<Array<IdNameObject> | null>(null);

  const graphOptions: Array<GraphLayoutProps> = [
    {
      id: 'area',
      name: 'Area',
      type: 'area'
    },
    {
      id: 'bar',
      name: 'Bar (Side by Side)',
      type: 'column'
    },
    {
      id: 'bar-stacked',
      name: 'Bar (Stacked)',
      type: 'column'
    },
    {
      id: 'line',
      name: 'Line',
      type: 'line'
    }
  ];

  useEffect(() => {
    setSliceByValue(dimensions[0].name);
    setValuePercentage(valuePercentageOptions[0]);
    setGraphLayout(graphOptions[0]);
  }, [dimensions]);

  useEffect(() => {
    const dimension = dimensions.filter((d) => d.name === sliceByValue);
    if (dimension.length) {
      setDimensionMetrics(dimension[0].metrics);
    }
  }, [dimensions, sliceByValue]);

  useEffect(() => {
    if (dimensionMetrics && selectedMetric) {
      const slectedMetricIndex = dimensionMetrics.findIndex((metric) => metric.name === selectedMetric.name);
      if (slectedMetricIndex > -1) {
        setSelectedMetric(dimensionMetrics[slectedMetricIndex]);
      } else {
        setSelectedMetric(defaultMetric);
      }
    }
  }, [dimensionMetrics]);

  useEffect(() => {
    if (sliceByValue && selectedMetric && valuePercentage) {
      const selectedOptions: SlicerSettings = {
        sliceBy: sliceByValue,
        metric: selectedMetric
      };
      onUpdateSettings(selectedOptions);
    }
  }, [selectedMetric, sliceByValue]);

  useEffect(() => {
    valuePercentage && onUpdateValuePercentage(valuePercentage.id);
  }, [valuePercentage]);

  useEffect(() => {
    graphLayout && onUpdateGraphLayout(graphLayout);
  }, [graphLayout]);

  useEffect(() => {
    const options = isPercentageValue ? onlyValuePercentageOption : defaultValuePercentageOptions;
    setValuePercentageOptions(options);
    if (isPercentageValue && valuePercentage !== options[0]) setValuePercentage(options[0]);
  }, [isPercentageValue]);

  const updateValuePercentage = (option: string) => {
    const selected = valuePercentageOptions.filter((o) => o.name === option);
    setValuePercentage(selected[0]);
  };
  const updateGraphLayout = (option: string) => {
    const selected = graphOptions.filter((o) => o.name === option);
    setGraphLayout(selected[0]);
  };
  const updateSelectedMetric = (option: string) => {
    if (dimensionMetrics) {
      const selected = dimensionMetrics.filter((o) => o.name === option);
      setSelectedMetric(selected[0]);
    }
  };

  const wrapperStyles = 'w-full mr-[10px]';
  const labelStyles = 'text-xs leading-4 text-[#2b2b2b] tracking-[0.1px] uppercase m-0 font-nunitoBold mb-[8px]';
  return (
    <div className="flex justify-between mt-[20px] ml-[16px] mr-[6px]">
      {sliceByValue && (
        <div className={wrapperStyles}>
          <p className={labelStyles}>Slice by:</p>
          <DropDownFilter
            label={''}
            options={dimensions.map((o) => o.name)}
            onChange={(option: string) => setSliceByValue(option)}
            value={sliceByValue}
            variant={'slicerMenu'}
            automationID="slicer_dropdown_sliceby"
            disabled={disableMenu}
            scrollable={dimensions?.length > 8 ? true : false}
          />
        </div>
      )}
      {dimensionMetrics && selectedMetric && (
        <div className={wrapperStyles}>
          <p className={labelStyles}>Metric:</p>
          <DropDownFilter
            label={''}
            options={dimensionMetrics.map((o) => o.name)}
            onChange={(option: string) => updateSelectedMetric(option)}
            value={selectedMetric.name}
            variant={'slicerMenu'}
            automationID="slicer_dropdown_metric"
            disabled={disableMenu}
            scrollable={dimensionMetrics?.length > 8 ? true : false}
          />
        </div>
      )}
      {valuePercentage && (
        <div className={wrapperStyles}>
          <p className={labelStyles}>Value or Percentage:</p>
          <DropDownFilter
            label={''}
            options={valuePercentageOptions.map((o) => o.name)}
            onChange={(option: string) => updateValuePercentage(option)}
            value={valuePercentage.name}
            variant={'slicerMenu'}
            automationID="slicer_dropdown_valuepercentage"
            disabled={disableMenu}
            scrollable={valuePercentageOptions?.length > 8 ? true : false}
          />
        </div>
      )}
      {graphLayout && (
        <div className={wrapperStyles}>
          <p className={labelStyles}>View As:</p>
          <DropDownFilter
            label={''}
            options={graphOptions.map((o) => o.name)}
            onChange={(option: string) => updateGraphLayout(option)}
            value={graphLayout.name}
            variant={'slicerMenu'}
            automationID="slicer_dropdown_viewas"
            disabled={disableMenu}
            scrollable={graphOptions?.length > 8 ? true : false}
          />
        </div>
      )}
    </div>
  );
};

export default SlicerMenu;
